import React, { useEffect, useState } from 'react';
import { Check, EyeOff, Trash2, ExternalLink, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '@/lib/api';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'hidden', label: 'Hidden' },
];

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  hidden: 'bg-gray-100 text-gray-700 border-gray-200',
};

export default function AdminQuestions() {
  const [status, setStatus] = useState('all');
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = () => {
    const params = { limit: 100 };
    if (status !== 'all') params.status = status;
    api.get('/admin/questions', { params }).then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const moderate = async (id, newStatus) => {
    try { await api.put(`/admin/questions/${id}/moderate`, { status: newStatus }); toast.success(`Marked ${newStatus}`); load(); }
    catch { toast.error('Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try { await api.delete(`/admin/questions/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };
  const answer = async (id) => {
    const text = (drafts[id] || '').trim();
    if (!text) { toast.error('Answer cannot be empty'); return; }
    try { await api.post(`/admin/questions/${id}/answer`, { answer: text }); setDrafts((d) => ({ ...d, [id]: '' })); toast.success('Answer posted'); load(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold">Product Q&amp;A</h1>
      <p className="text-sm text-muted-foreground">Answer customer questions and moderate visibility.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setStatus(t.key)} className={`rounded-full border border-border px-4 py-1.5 text-xs font-semibold ${status === t.key ? 'bg-[hsl(var(--primary))] text-white' : 'bg-white text-foreground'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-3" data-testid="admin-questions-list">
        {data?.items?.map((q) => (
          <div key={q.id} className="rounded-2xl border border-border bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <Link to={`/product/${q.product_slug}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--primary))]">{q.product_name || q.product_slug} <ExternalLink size={11} /></Link>
                <p className="mt-1 flex items-start gap-2 text-sm"><MessageSquare size={14} className="mt-0.5 shrink-0" /> <span className="font-medium">{q.question}</span></p>
                <p className="mt-1 text-xs text-muted-foreground">Asked by {q.name} · {new Date(q.created_at).toLocaleString()}</p>
              </div>
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_STYLES[q.status] || 'bg-gray-50'}`}>{q.status || 'approved'}</span>
            </div>

            {q.answers?.length > 0 && (
              <div className="mt-3 space-y-1 rounded-xl bg-[hsl(var(--gaze-ivory))] p-3 text-xs">
                {q.answers.map((a) => (
                  <div key={a.id}><span className="font-semibold">{a.author}:</span> {a.text}</div>
                ))}
              </div>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input value={drafts[q.id] || ''} onChange={(e) => setDrafts({ ...drafts, [q.id]: e.target.value })} placeholder="Type an official answer…" className="min-w-[240px] flex-1 rounded-lg border border-border px-3 py-2 text-sm" data-testid="admin-question-answer" />
              <button onClick={() => answer(q.id)} className="rounded-full bg-[hsl(var(--primary))] px-4 py-2 text-xs font-semibold text-white">Answer</button>
              {q.status !== 'approved' && <button onClick={() => moderate(q.id, 'approved')} className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-2 text-xs font-semibold"><Check size={12} /> Approve</button>}
              {q.status !== 'hidden' && <button onClick={() => moderate(q.id, 'hidden')} className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-2 text-xs font-semibold"><EyeOff size={12} /> Hide</button>}
              <button onClick={() => del(q.id)} className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-2 text-xs font-semibold text-destructive"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
        {data && data.items.length === 0 && <p className="rounded-2xl border border-dashed border-border bg-white p-10 text-center text-sm text-muted-foreground">No questions yet.</p>}
      </div>
    </div>
  );
}
